import classNames from 'classnames';
// CORE
import React, { memo } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ButtonBase from '@mui/material/ButtonBase';
import type { ScheduledTransaction } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import type { CashFlowTransaction, QcsColor } from 'app-components/flux/transactions/transactionsTypes';
// CUSTOM COMPONENTS
import FlagField from 'components/Transactions/FlagField';
import RecurringLinkRow from 'components/Transactions/RecurringLinkRow';
import StatusField from 'components/Transactions/StatusField';
// MUI
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  txnHeaderSection: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(3),
  },
  statusField: {
    maxWidth: '158px',
    flex: 1.5,
    margin: 0,
    marginLeft: '12px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: 24,
  },
  checkIcon: {
    height: 56,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    padding: '12px 16px',
    color: theme.palette.icon.light,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  checkIconBackground: {
    backgroundColor: theme.palette.background.paper,
  },
  checkIconBackgroundSelected: {
    backgroundColor: theme.palette.success.light,
  },
  checkIconHover: {
    color: `${theme.palette.amount.positive} !important`,
  },
  upperLeftButtonDiv: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: 348,
    gap: 12,
  },
  recurringButtonClass: {
    width: '100%',
    textAlign: 'right',
  },
}));

function TxnHeaderSection({
  txn,
  model,
  create,
  status,
  isBill,
  unMarkAsBill,
  markRecurring,
  setFlag,
  flag,
  viewRecurring,
  disabledRecurring,
  setEditSeries,
  updateReviewed,
  reviewed,
  setStatus,
  unlinkRecurring,
  disabled,
  cpSection,
}: {
  txn: CashFlowTransaction;
  model: ScheduledTransaction | undefined;
  create: boolean;
  status: 'CLEARED' | 'PENDING' | 'RECONCILED' | undefined;
  isBill: boolean | null | undefined;
  unMarkAsBill: () => void;
  markRecurring: () => void;
  setFlag: (val: QcsColor | null | undefined) => void;
  flag: QcsColor | null | undefined;
  viewRecurring: () => void;
  disabledRecurring: boolean;
  setEditSeries: () => void;
  updateReviewed: () => void;
  reviewed: boolean | null | undefined;
  setStatus: (val: string) => void;
  unlinkRecurring: () => void;
  disabled: boolean;
  /** Connected-account “appears on statement” line; rendered above the status/recurring row. */
  cpSection?: React.ReactNode;
}) {
  const { classes } = useStyles();

  const allowStatus = create || !disabled || status !== 'CLEARED' || txn.subtype === 'SALES_RECEIPT';

  return (
    <div id='txn-header-section' className={classes.txnHeaderSection}>
      {cpSection}
      <div className={classes.row}>
        <div className={classes.upperLeftButtonDiv}>
          <ButtonBase
            key={`${txn.id}-reviewed`}
            id={`${txn.id}-reviewed`}
            onClick={updateReviewed}
            className={classNames(
              classes.checkIcon,
              reviewed ? classes.checkIconBackgroundSelected : classes.checkIconBackground,
            )}
          >
            {reviewed ? <CheckCircleIcon className={classes.checkIconHover} /> : <CheckCircleOutlineIcon />}
          </ButtonBase>
          <FlagField plain paddedFlagButton value={flag} onChange={setFlag} />

          {/* Status Field */}
          <StatusField
            status={status}
            onChange={setStatus}
            className={classes.statusField}
            readOnly={!allowStatus}
            margin='none'
            variant='filled'
          />
        </div>
        <div className={classes.recurringButtonClass}>
          <RecurringLinkRow
            txn={txn}
            model={model}
            isBill={isBill}
            unMarkAsBill={unMarkAsBill}
            markRecurring={markRecurring}
            viewRecurring={viewRecurring}
            disabledRecurring={disabledRecurring}
            setEditSeries={setEditSeries}
            unlinkRecurring={unlinkRecurring}
            variant='inline'
          />
        </div>
      </div>
    </div>
  );
}

export default memo(TxnHeaderSection);
